import { useEffect, useState } from 'react'
import { subscriptionApi, type SubscriptionStatus, type UsageMetric } from '../services/api'

/**
 * The profile menu's "This month" usage (MAR-317; widened to every metered allowance and the
 * bill in MAR-413).
 *
 * The same read Account → Billing makes (`subscriptionApi.getStatus`), taken lazily — only
 * once the menu is opened, so the shell does not bill a request to every page load — and
 * then CACHED per account for five minutes (PR #321 reviews): reopening the menu within that
 * does not re-read, and after it the next open reads again — so a tab left open for days
 * never keeps last period's count, and the menu cannot drift far from Billing, which still
 * reads fresh. A failed or refused read is not cached, so the next open tries again.
 *
 * ⚠️ Quiet by design: the menu is not the place to report a billing failure. Loading, an
 * error, a refused read, or a plan with no metered MAU limit all return `null` and the
 * section is simply absent — Billing is where the merchant goes to see why. A conversation
 * or email metric that is missing or unusable is dropped on its own; the rest still shows.
 *
 * ⚠️ Scoped at the READ: the cache and the stored value are keyed by account, and a value
 * read for another account is never returned. Switching workspace to one under another
 * account must not show the previous account's count for a frame.
 *
 * The caller gates it on role (`canAccessFeature(role, 'account')`): Account refuses members
 * and viewers, and `subscription-api` has no role check of its own, so `enabled` must be
 * false for them or the menu would show what the billing page will not.
 */
export interface MonthUsage {
  /** "Starter" — the tier's own name, for "This month · Starter". */
  tierName: string
  /** 'trial' while trialing, 'ended' once the trial expired, else null. A trial has no bill. */
  trial: 'trial' | 'ended' | null
  /** The subscription was cancelled: no bill, no upgrade. */
  cancelled: boolean
  /**
   * Whether "N more and chat pauses" is a true statement about these numbers: the account has
   * exactly ONE workspace and the nightly enforcement acts on it. The usage below is summed over
   * the account's workspaces while the overage pause is decided per workspace, and enforcement
   * skips synthetic, shadow and disabled ones (`isUsageEnforced` in `@marva/shared/billing`).
   * Both counts come from the server; a server that does not send them makes this false.
   * ⚠️ Never the caller's memberships (`workspaceStore.workspaces`): an admin who is a member of
   * one of three workspaces would read 1.
   */
  pausable: boolean
  /** When a running trial ends. ISO, or null. */
  trialEndsAt: string | null
  /**
   * When the conversation and email counters reset: the first of next month, UTC.
   * ⚠️ NOT the Stripe period end. The counters are keyed by calendar month
   * (`getCurrentPeriod`, `YYYY-MM`, in `@marva/shared/shadow-interceptor`) whatever the billing
   * anniversary or interval, and the overage pause lifts at that rollover. An annual plan's
   * `period.end` is a year out; a monthly one's is usually mid-month.
   */
  resetsAt: string
  mau: UsageMetric
  conversations: UsageMetric | null
  emails: UsageMetric | null
  /**
   * Base + metered overage so far — shown only for a live monthly subscription (`active` or
   * `past_due`). Null for Enterprise (invoiced), a trial, a cancellation, and an annual plan,
   * whose `billEstimate` still carries the MONTHLY base price and whose overage is collected at
   * renewal, so "bill so far this month" would be a number nobody is charged.
   */
  bill: { baseCents: number; overageCents: number; totalCents: number } | null
  /** The next self-serve tier's name ("Scale"), or null when there is none to move to. */
  nextTier: string | null
}

const tierName = (tier: string | undefined) => (tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '')

const usable = (m: UsageMetric | undefined): m is UsageMetric =>
  !!m && Number.isFinite(m.used) && Number.isFinite(m.limit) && m.limit > 0 && m.used >= 0

/** The tier after the current one in `availableTiers`, unless it is sales-led (Enterprise). */
export function nextTierOf(status: Pick<SubscriptionStatus, 'tier' | 'availableTiers'>): string | null {
  const tiers = status.availableTiers ?? []
  const at = tiers.findIndex((t) => t.id === status.tier)
  const next = at >= 0 ? tiers[at + 1] : undefined
  return next && !next.contactSales ? next.name : null
}

/** The first instant of the calendar month after `now`, UTC — when the usage counters roll over. */
export function nextMonthStart(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString()
}

/** A billing period longer than any month: an annual subscription. */
const isAnnual = (period: SubscriptionStatus['period'] | undefined) => {
  const start = period?.start ? Date.parse(period.start) : NaN
  const end = period?.end ? Date.parse(period.end) : NaN
  return Number.isFinite(start) && Number.isFinite(end) && end - start > 40 * 24 * 60 * 60 * 1000
}

function monthUsageOf(data: SubscriptionStatus | undefined): MonthUsage | null {
  if (!data || !usable(data.usage?.mau)) return null
  const trial = data.status === 'trialing' ? 'trial' : data.status === 'expired' ? 'ended' : null
  const billable = (data.status === 'active' || data.status === 'past_due') && !isAnnual(data.period)
  return {
    tierName: tierName(data.tier),
    trial,
    cancelled: data.status === 'cancelled',
    pausable: data.workspaceCount === 1 && data.enforcedWorkspaceCount === 1,
    trialEndsAt: trial === 'trial' ? data.period?.endsAt ?? null : null,
    resetsAt: nextMonthStart(new Date(Date.now())),
    mau: data.usage.mau,
    conversations: usable(data.usage.conversations) ? data.usage.conversations : null,
    emails: usable(data.usage.emails) ? data.usage.emails : null,
    bill: billable && data.billEstimate ? data.billEstimate : null,
    nextTier: nextTierOf(data),
  }
}

/** How long a settled read stands before the next open re-reads it. */
export const MONTH_USAGE_TTL_MS = 5 * 60 * 1000

/** Settled reads, per account, with when they landed. `value: null` = read fine, nothing to meter. */
const settled = new Map<string, { value: MonthUsage | null; at: number }>()

/**
 * Bumped by every reset. A hook's own last read carries the epoch it landed in, and is shown only
 * while that epoch is current and the read is inside its TTL — otherwise a reopened menu would
 * show the hook's stale `read` (the old plan, its card and its upgrade) while the new read is in
 * flight (review round 3).
 */
let epoch = 0

/** A hook's own last read is still good: it landed in this epoch and inside its TTL. */
const current = (read: { at: number; epoch: number }) => read.epoch === epoch && Date.now() - read.at < MONTH_USAGE_TTL_MS

/** A settled read still inside its TTL, or undefined. */
const fresh = (accountId: string) => {
  const hit = settled.get(accountId)
  return hit && Date.now() - hit.at < MONTH_USAGE_TTL_MS ? hit : undefined
}

/**
 * Forget the session's reads. Called after a plan changes in place (`PricingView`), so the menu
 * does not show the old plan, its over-plan card and the same upgrade for up to five minutes.
 * A Stripe Checkout upgrade returns through a full page load, which starts with no cache.
 */
export function resetMonthUsageCache(): void {
  settled.clear()
  epoch += 1
}

export function useMonthUsage(accountId: string | undefined, enabled: boolean): MonthUsage | null {
  const [read, setRead] = useState<{ accountId: string; value: MonthUsage | null; at: number; epoch: number } | null>(
    null
  )

  useEffect(() => {
    if (!enabled || !accountId || fresh(accountId)) return
    let live = true
    // A read that was in flight when the cache was reset (a plan changed in place meanwhile)
    // describes the old plan: it is dropped, never cached as current (review round 4).
    const startEpoch = epoch
    subscriptionApi
      .getStatus(accountId)
      .then((res) => {
        if (startEpoch !== epoch) return
        const at = Date.now()
        if (!res?.success) {
          if (live) setRead({ accountId, value: null, at, epoch })
          return
        }
        const value = monthUsageOf(res.data)
        settled.set(accountId, { value, at })
        if (live) setRead({ accountId, value, at, epoch })
      })
      .catch(() => {
        if (live && startEpoch === epoch) setRead({ accountId, value: null, at: Date.now(), epoch })
      })
    return () => {
      live = false
    }
  }, [accountId, enabled])

  if (!enabled || !accountId) return null
  const hit = fresh(accountId)
  if (hit) return hit.value
  return read && read.accountId === accountId && current(read) ? read.value : null
}
